import React from 'react';
import { getThemeStyles } from '../../styles/themes';
import { extractColorFromClass } from './colorUtils';

interface ThemePreviewProps {
  themeId: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Visual preview of a theme showing its colors in a mini website layout
 * Similar to BrandPackagePreview for consistent UI
 */
export const ThemePreview: React.FC<ThemePreviewProps> = ({
  themeId,
  size = 'medium',
  className = '',
}) => {
  const theme = getThemeStyles(themeId);

  // Size configurations (matching BrandPackagePreview)
  const sizeConfig = {
    small: {
      container: 'w-full',
      navbarHeight: 'h-3',
      footerHeight: 'h-3',
      headingSize: 'text-[10px]',
      bodySize: 'text-[8px]',
      buttonSize: 'text-[7px] px-1.5 py-0.5',
      badgeSize: 'text-[6px] px-1 py-0.5',
      padding: 'p-1.5',
      gap: 'gap-0.5',
      cardPadding: 'p-1',
      starSize: 'w-1.5 h-1.5',
    },
    medium: {
      container: 'w-full',
      navbarHeight: 'h-4',
      footerHeight: 'h-4',
      headingSize: 'text-xs',
      bodySize: 'text-[10px]',
      buttonSize: 'text-[8px] px-2 py-0.5',
      badgeSize: 'text-[7px] px-1.5 py-0.5',
      padding: 'p-2',
      gap: 'gap-1',
      cardPadding: 'p-1.5',
      starSize: 'w-2 h-2',
    },
    large: {
      container: 'w-full',
      navbarHeight: 'h-5',
      footerHeight: 'h-5',
      headingSize: 'text-sm',
      bodySize: 'text-xs',
      buttonSize: 'text-[10px] px-2.5 py-1',
      badgeSize: 'text-[8px] px-2 py-0.5',
      padding: 'p-3',
      gap: 'gap-1.5',
      cardPadding: 'p-2',
      starSize: 'w-2.5 h-2.5',
    },
  };

  const config = sizeConfig[size];

  // Extract colors from theme, handling both hex values and Tailwind classes
  const getColor = (value: string | undefined, fallback: string): string => {
    if (!value) return fallback;
    if (value.startsWith('#')) return value;
    return extractColorFromClass(value);
  };

  // Get colors from theme with fallbacks
  const navbarBg = getColor(theme.navbar?.background, '#ffffff');
  const navbarText = getColor(theme.navbar?.text, '#111827');
  const heroBg = getColor(theme.hero?.background, '#3b82f6');
  const heroText = getColor(theme.hero?.text, '#ffffff');
  const featuresBg = getColor(theme.features?.background, '#f9fafb');
  const cardBg = getColor(theme.features?.cardBg, '#ffffff');
  const iconColor = getColor(theme.features?.iconColor, '#3b82f6');
  const footerBg = getColor(theme.footer?.background, '#1f2937');
  const footerText = getColor(theme.footer?.text, '#ffffff');
  const accentColor = getColor(theme.testimonials?.starColor, '#fbbf24');

  // Check if hero background is a gradient
  const isGradient = typeof heroBg === 'string' && heroBg.includes('gradient');
  // For gradients, extract the primary color
  const heroBgStyle = isGradient ? heroBg : undefined;
  const heroBgColor = isGradient ? getColor(theme.hero?.background, '#3b82f6') : heroBg;

  return (
    <div
      className={`rounded-lg overflow-hidden border border-gray-200 shadow-sm flex flex-col ${config.container} ${className}`}
    >
      {/* Navbar */}
      <div
        className={`${config.navbarHeight} flex items-center justify-between px-2 shrink-0`}
        style={{ backgroundColor: navbarBg }}
      >
        <div className="w-6 h-1 rounded-full opacity-90" style={{ backgroundColor: navbarText }} />
        <div className="flex gap-1 items-center">
          <div
            className="w-3 h-0.5 rounded-full opacity-60"
            style={{ backgroundColor: navbarText }}
          />
          <div
            className="w-3 h-0.5 rounded-full opacity-60"
            style={{ backgroundColor: navbarText }}
          />
          <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: iconColor }} />
        </div>
      </div>

      {/* Hero section */}
      <div
        className={`${config.padding} flex flex-col ${config.gap} flex-1 min-h-0`}
        style={{
          background: heroBgStyle,
          backgroundColor: !isGradient ? heroBgColor : undefined,
        }}
      >
        {/* Badge */}
        <div
          className={`${config.badgeSize} rounded-full self-start font-medium`}
          style={{
            backgroundColor: iconColor,
            color: '#ffffff',
          }}
        >
          New
        </div>

        {/* Heading */}
        <div
          className={`${config.headingSize} font-bold leading-tight`}
          style={{ color: heroText }}
        >
          Welcome
        </div>

        {/* Body text */}
        <div className={`${config.bodySize} opacity-70 leading-tight`} style={{ color: heroText }}>
          Your tagline here
        </div>

        {/* Buttons */}
        <div className="flex gap-1 mt-auto">
          <div
            className={`${config.buttonSize} rounded font-medium`}
            style={{
              backgroundColor: iconColor,
              color: '#ffffff',
            }}
          >
            Get Started
          </div>
          <div
            className={`${config.buttonSize} rounded font-medium border`}
            style={{
              borderColor: heroText,
              color: heroText,
              opacity: 0.7,
            }}
          >
            Learn
          </div>
        </div>
      </div>

      {/* Features row */}
      <div
        className={`${config.padding} flex ${config.gap} shrink-0`}
        style={{ backgroundColor: featuresBg }}
      >
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={`flex-1 ${config.cardPadding} rounded border flex flex-col items-center`}
            style={{
              backgroundColor: cardBg,
              borderColor: '#e5e7eb',
            }}
          >
            {/* Icon */}
            <div
              className={`${config.starSize} rounded-sm mb-0.5`}
              style={{ backgroundColor: iconColor }}
            />
            {/* Stars */}
            <div className="flex gap-px">
              {[1, 2, 3].map(star => (
                <div
                  key={star}
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: accentColor }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        className={`${config.footerHeight} flex items-center justify-center px-2 shrink-0`}
        style={{ backgroundColor: footerBg }}
      >
        <div className="flex gap-2 items-center">
          <div
            className="w-4 h-0.5 rounded-full opacity-50"
            style={{ backgroundColor: footerText }}
          />
          <div
            className="w-4 h-0.5 rounded-full"
            style={{ backgroundColor: iconColor, opacity: 0.8 }}
          />
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
